var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import * as React from "react";
import { IntlProvider, useIntl } from "react-intl";
import { enUS } from "date-fns/locale";
import { DatePicker, registerLocale } from "@bolteu/kalep-react";
import { ComponentServices } from "../../ComponentServices";
import { FormattedMessage } from "../../utils/FormattedMessage";
import { TimezoneFormatter } from "../../utils/TimezoneFormatter";
registerLocale("en-US", enUS);
var popperPlacementMap = {
  left: "bottom-start",
  right: "bottom-end"
};
var typeFormat = {
  date: "dd/MM/yyyy",
  datetime: "dd/MM/yyyy HH:mm",
  time: "HH:mm"
};
function createTimePicker(type, toValue, fromValue) {
  var shouldShowTime = type === "datetime" || type === "time";
  var shouldShowTimeOnly = type === "time";
  var format = typeFormat[type];
  return function TimePickerComponent(props) {
    var popperPlacement = props.arrow && popperPlacementMap[props.arrow];
    var intl = useIntl();
    var _React$useContext = React.useContext(ComponentServices.Context),
      timezoneService = _React$useContext.timezoneService;
    var timezone = React.useMemo(function () {
      var _a, _b;
      return (_b = (_a = props.timezone) !== null && _a !== void 0 ? _a : intl.timeZone) !== null && _b !== void 0 ? _b : "UTC";
    }, [intl.timeZone, props.timezone]);
    var fieldValue = props.value,
      isDisabled = props.disabled,
      validatorResults = props.validatorResults,
      onChange = props.onChange,
      hasNoTimezoneMessage = props.hasNoTimezoneMessage,
      shouldDisableFuture = props.disableFuture,
      shouldDisablePast = props.disablePast,
      minDate = props.minDate,
      maxDate = props.maxDate,
      label = props.label,
      name = props.name,
      isCompact = props.compact,
      injectTimes = props.injectTimes,
      popperModifiers = props.popperModifiers,
      size = props.size,
      rest = __rest(props, ["value", "disabled", "validatorResults", "onChange", "hasNoTimezoneMessage", "disableFuture", "disablePast", "minDate", "maxDate", "label", "name", "compact", "injectTimes", "popperModifiers", "size"]);
    var validationError = validatorResults === null || validatorResults === void 0 ? void 0 : validatorResults[0];
    // in case of UnixTimestampField it converts the timestamp (number) into Date (taking into account tz)
    var value = React.useMemo(function () {
      return fromValue(fieldValue, timezoneService.getTimezoneOffset(timezone, typeof fieldValue === "number" ? fieldValue : undefined));
    }, [fieldValue, timezone, timezoneService]);
    var onChangeHandler = React.useCallback(function (date) {
      if (Array.isArray(date)) {
        // Ignore ranges for now. Form-React does not expose this feature, despite underlying library supporting it.
        return;
      }
      if (date && Number.isNaN(date.getTime())) {
        return;
      }
      // To support props.defaultTime. - https://github.com/bolteu/admin-framework-components/pull/115/files
      if (!value && date && props.defaultTime) {
        date.setHours(props.defaultTime.hours);
        date.setMinutes(props.defaultTime.minutes);
      }
      onChange({
        value: date ? toValue(date, timezoneService.getTimezoneOffset(timezone, date.getTime() / 1000)) : undefined
      });
    }, [onChange, props.defaultTime, timezone, timezoneService, value]);
    var timezoneText = React.useMemo(function () {
      if (hasNoTimezoneMessage || type === "time") {
        return null;
      }
      return /*#__PURE__*/React.createElement(FormattedMessage, {
        id: "common.timezoneInformation",
        values: {
          timezone: ( /*#__PURE__*/React.createElement(TimezoneFormatter, {
            value: timezone,
            unix: value ? value.getTime() / 1000 : undefined
          }))
        }
      });
    }, [timezone, value, hasNoTimezoneMessage]);
    var minDateValue = React.useMemo(function () {
      if (shouldDisablePast) {
        return new Date();
      }
      if (minDate) {
        return minDate;
      }
      return undefined;
    }, [shouldDisablePast, minDate]);
    var maxDateValue = React.useMemo(function () {
      if (shouldDisableFuture) {
        return new Date();
      }
      if (maxDate) {
        return maxDate;
      }
      return undefined;
    }, [shouldDisableFuture, maxDate]);
    return /*#__PURE__*/React.createElement(IntlProvider, {
      locale: intl.locale,
      messages: intl.messages,
      timeZone: timezone
    }, /*#__PURE__*/React.createElement(DatePicker, {
      label: label,
      placeholder: rest.placeholder,
      helperText: validationError ? validationError.error : timezoneText,
      isDisabled: isDisabled,
      isError: !!validationError,
      value: value,
      hasTimeSelect: shouldShowTime,
      injectTimes: injectTimes,
      hasTimeSelectOnly: shouldShowTimeOnly,
      minDate: minDateValue,
      maxDate: maxDateValue,
      format: format,
      required: rest.required,
      name: String(name),
      onChange: onChangeHandler,
      popperPlacement: popperPlacement,
      popperModifiers: popperModifiers,
      size: isCompact ? "sm" : size,
      fullWidth: rest.fullWidth === undefined ? true : rest.fullWidth
    }));
  };
}
function toUnixTimestamp(input, utcOffsetForTimezone) {
  if (!input) {
    return input;
  }
  // Input is always has browser timezone offset O1
  // The actual input needs to in target timezone with offset O2
  // Which makes the whole time O2 - O1 minutes different
  return input.getTime() / 1000 + (utcOffsetForTimezone - input.getTimezoneOffset()) * 60;
}
function fromUnixTimestamp(input, utcOffsetForTimezone) {
  if (!input) {
    return null;
  }
  // Input is always UTC +0
  // Date always takes account browser local browser timestamp offset O1
  // In order to move actual date to target timestamp with offset O2
  // we need to shift time O2 - O1
  var date = new Date(input * 1000);
  date.setMinutes(date.getMinutes() - (utcOffsetForTimezone - date.getTimezoneOffset()));
  return date;
}
function toDate(input) {
  if (!input) {
    return null;
  }
  var month = String(input.getMonth() + 1).padStart(2, "0");
  var day = String(input.getDate()).padStart(2, "0");
  var year = input.getFullYear();
  // Date time in format YYYY-MM-DD in the backend with tDate
  // export type tDate = string & { __is_date: never };
  // Using YYYY-MM-DD format to be compliant with backend
  return [year, month, day].join("-");
}
function fromDate(input) {
  return input ? new Date("".concat(input, "T00:00")) : null;
}
function toTime(input) {
  return input ? input.getHours() * 60 + input.getMinutes() : null;
}
function fromTime(input) {
  if (input === undefined || input === null) {
    return null;
  }
  var date = new Date();
  date.setHours(Math.floor(input / 60));
  date.setMinutes(input % 60);
  return date;
}
function toTimeWithSeconds(input) {
  return input ? input.getHours() * 3600 + input.getMinutes() * 60 + input.getSeconds() : null;
}
function fromTimeWithSeconds(input) {
  if (input === undefined || input === null) {
    return null;
  }
  var date = new Date();
  date.setHours(Math.floor(input / 3600));
  date.setMinutes(Math.floor(input % 3600 / 60));
  date.setSeconds(input % 60);
  return date;
}
export var UnixTimestampField = createTimePicker("datetime", toUnixTimestamp, fromUnixTimestamp);
export var DateField = createTimePicker("date", toDate, fromDate);
export var TimeField = createTimePicker("time", toTime, fromTime);
export var TimeFieldWithSeconds = createTimePicker("time", toTimeWithSeconds, fromTimeWithSeconds);